/**
 * Small shared UI helpers used across the Upright chat module:
 * typing indicator, assistant-message footer, and MCP-app iframe wrapper.
 *
 * Kept separate from the tool-UI definitions so the adapter / composer /
 * welcome modules can depend on these without pulling in the full tool-UI
 * surface.
 */
import { useEffect, useState } from 'react';
import { useAssistantRuntime, useMessage } from '@assistant-ui/react';
import { useRef } from 'react';
import { McpAppIframe, type McpAppIframeHandle } from './mcp-app-iframe';
import { useLiveJobStatus } from './use-live-job-status';
import { getMcpAppCardPreview } from './mcp-app-card-preview';
import { isTrailOnlyResult, isTrailPart } from './tool-trail-steps';

/**
 * Dispatched on the thread viewport to ask the scroll lock in upright-chat.tsx to follow the
 * thread to the bottom, as if the user had scrolled there themselves. The lock owns the scroll
 * policy, so anything that moves the viewport on the user's behalf goes through it.
 */
export const FOLLOW_THREAD_EVENT = 'upright-chat:follow-thread';

/**
 * Three-dot bouncing indicator shown while the assistant is thinking.
 */
export function TypingDots() {
  return (
    <div className="typing-indicator" aria-label="Assistant is thinking">
      <span className="typing-dot" />
      <span className="typing-dot" />
      <span className="typing-dot" />
    </div>
  );
}

/**
 * Footer for assistant messages — shows typing dots while the message
 * is still running but only when the tool trail is not already showing a step in progress.
 */
export function TypingFooter() {
  const message = useMessage(state => state);
  if (message.status?.type !== 'running') return null;
  // PLT-7912: the tool trail's line shows the step that is running, quiet reads included.
  const hasRunningStep = message.content.some(p => isTrailPart(p) && p.type === 'tool-call' && p.result === undefined);
  if (hasRunningStep) return null;
  // If an HTML report is actively streaming, the panel already shows progress —
  // hide the redundant dots in the chat thread. We're already inside the
  // `status.type === 'running'` branch, so a `_partial` result here means the
  // preview is in flight (the final tool-result fully replaces the object,
  // dropping `_partial`).
  const hasStreamingReport = message.content.some((p: any) => p.type === 'tool-call' && p.result?._partial);
  if (hasStreamingReport) return null;
  return <TypingDots />;
}

/**
 * Pull a `jobId` field out of an arbitrary tool-result payload, returning
 * `undefined` if the payload doesn't carry one. The DD CompanyCard /
 * ProductSetCard payloads (PLT-4621 / PLT-4622) carry `jobId` at the top
 * level; every other tool result is `jobId`-free, so the live-update path
 * silently no-ops for them.
 */
function extractJobId(payload: unknown): number | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const candidate = (payload as { jobId?: unknown }).jobId;
  return typeof candidate === 'number' && Number.isFinite(candidate) ? candidate : undefined;
}

/**
 * MCP App iframe wrapper that connects the iframe's send-message protocol
 * to the assistant-ui runtime, allowing apps to inject user messages into chat.
 *
 * PLT-4622: when the tool result carries a `jobId`, mount `useLiveJobStatus`
 * to push live deltas into the iframe via the `postLiveUpdate` handle. The
 * iframe absorbs the deltas through its own state machine (no remount), so
 * the CompanyCard's "computing → ready" transition happens in place. Other
 * tool results — and Claude / non-Upright hosts where this wrapper isn't
 * mounted at all — render the static payload unchanged.
 *
 * Tall cards stacked in one response turn the thread into a wall of large
 * tables before the user reaches the assistant's prose (pilot feedback,
 * PLT-4997). So the iframe is wrapped in a `.chat-mcp-app-card` shell with
 * a clickable toggle row above it (label + chevron + Show/Hide) that
 * collapses the body on every surface — wide `/chat`, the side panel, and
 * narrow viewports alike (pre-PLT-4997 the toggle was hidden and the body
 * force-shown on wide `/chat`, pinning those cards open).
 *
 * The body element (and the iframe inside it) is always present in the
 * DOM — there is no lazy-mount. This keeps responsiveness simple under
 * window-resize / panel-toggle: the iframe stays alive across collapse /
 * expand and the user doesn't pay a reload cost when re-expanding. The
 * trade is that the iframe still fetches its bundle while collapsed (most
 * browsers load `display: none` iframes), but the bundle is small and
 * shared across all card instances on the page.
 *
 * Starting state (PLT-4997): a lone MCP-app card auto-expands (the user
 * asked to see exactly that one — `useIsSoleMcpAppCard`); cards in a
 * multi-card stack start collapsed. The per-tool `defaultExpanded` prop is
 * a hard override that keeps a card expanded even inside a stack — used by
 * the comparison cards (the summary the user is comparing across), the DD
 * lifecycle cards (they surface live job progress), and the value-chain
 * and impact pathway cards (their diagram IS the answer — PLT-4829,
 * PLT-7519 — and a collapsed diagram-only card reads as "the diagram never
 * drew"). Single-item lens
 * cards (net impact, DMA, single matter, …) pass no override and follow
 * the sole-card signal.
 *
 * This collapse only applies in our own web chat, which renders cards
 * through this wrapper. Third-party MCP hosts (Claude.ai, Claude Desktop)
 * mount the app bundle directly with no wrapper, so they always show the
 * full card — we don't control their layout.
 *
 * On intermediate+ chat widths the collapsed toggle row also shows a single
 * headline figure per card, right-aligned next to the Show affordance
 * (PLT-4997) — `+49%` for net impact, `9/16` material matters for DMA, `12/18`
 * for SFDR PAIs — so a stack of collapsed cards reads as a scannable column of
 * one figure per row rather than a list of bare labels. The figure is derived
 * by `getMcpAppCardPreview`; the CSS (`.chat-mcp-app-card-preview`) hides it on
 * mobile / in the narrow side panel and while the card is expanded (the open
 * body already carries the figure).
 *
 * `label` is a human-readable name for the placeholder (e.g. "Net Impact",
 * "DMA Results"). Falls back to a prettified `appName` if not supplied.
 */
function prettifyAppName(appName: string): string {
  return appName.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** The tools whose card asks the user to act (accept a proposal, pick tailorings), drawn without an MCP app. */
const ACTION_CARD_TOOLS: ReadonlySet<string> = new Set([
  'propose_tailoring_batch',
  'propose_operational_data_batch',
  'propose_sustainability_indicators_batch',
  'request_tailoring_selection',
  'request_own_insights_start',
  'propose_site_locations',
  'review_supplier_import',
  'request_upright_review',
  'propose_tailoring_removal',
]);

/**
 * True when the current assistant message contains at most one MCP-app
 * card (this one). Drives the sole-card auto-expand in `ChatMcpAppIframe`
 * (PLT-4997): a lone card — the "show the DMA for company X" case the user
 * explicitly asked to see — opens expanded, while the members of a
 * multi-card stack (one card per company, many DMA-topic cards, …) all
 * start collapsed so the response reads as a tidy list of headers instead
 * of a wall of large tables.
 *
 * Counts the cards the message shows: tool-call content parts whose result
 * carries `_ui.appName` — the same signal `McpToolUI` uses to decide a tool
 * renders as an app card — and the action cards, and short-circuits as soon
 * as a second one is seen. A background read (`quiet`) does not count: its
 * card is in the tool trail, closed (PLT-7912). Re-evaluates as the
 * message streams (the `content` selector returns a fresh array per
 * delta), so a card that lands first and is momentarily sole collapses
 * again once a sibling card arrives, as long as the user hasn't toggled it.
 */
function useIsSoleMcpAppCard(): boolean {
  return useMessage(state => {
    let count = 0;
    for (const part of state.content) {
      // A part a reopened chat rebuilt from the call alone draws no card, action card or not.
      if (part.type !== 'tool-call' || part.result === undefined || part.args.quiet === true) continue;
      if (isTrailOnlyResult(part.result)) continue;
      const appName = (part.result as { _ui?: { appName?: unknown } } | null)?._ui?.appName;
      if (appName || ACTION_CARD_TOOLS.has(part.toolName)) {
        count += 1;
        if (count > 1) return false;
      }
    }
    return true;
  });
}

export function ChatMcpAppIframe({
  appName,
  toolResult,
  label,
  defaultExpanded = false,
  bare = false,
}: {
  appName: string;
  toolResult: unknown;
  label?: string;
  /**
   * The card alone, without its toggle row: the tool trail opens a background read's card under
   * the read's row, which is already the toggle (PLT-7912).
   */
  bare?: boolean;
  /**
   * Hard override that keeps the card expanded by default even when it sits
   * in a multi-card stack (PLT-4997). Defaults to `false`, where the card
   * follows the sole-card auto-expand signal (`useIsSoleMcpAppCard`). Set to
   * `true` for cards that should stay open in a stack: the comparison cards
   * (the across-companies summary), the DD lifecycle cards (live job
   * progress), and the value-chain and impact pathway cards (their diagram
   * IS the answer — PLT-4829, PLT-7519). The user can still collapse any card
   * manually.
   */
  defaultExpanded?: boolean;
}) {
  const runtime = useAssistantRuntime();
  const iframeHandleRef = useRef<McpAppIframeHandle>(null);

  useEffect(() => {
    if (appName === 'financial-statement-import') {
      iframeHandleRef.current?.postLiveUpdate(toolResult);
    }
  }, [appName, toolResult]);

  // Auto-expand a lone card; collapse the members of a multi-card stack
  // (PLT-4997). A per-tool `defaultExpanded` (comparison / DD / value-chain /
  // impact pathway cards pass `true`) is a hard override that keeps the card open even
  // inside a stack.
  const isSoleCard = useIsSoleMcpAppCard();
  const autoExpanded = defaultExpanded || isSoleCard;
  // Track only the user's manual intent and derive the visible state from
  // it + `autoExpanded`. Storing a standalone `expanded` boolean would let
  // it drift from `autoExpanded` while tool calls are still streaming in —
  // the redundant-state pattern the repo's React conventions warn against.
  // `null` = "user hasn't decided", so follow the auto signal.
  const [userToggled, setUserToggled] = useState<boolean | null>(null);
  const expanded = userToggled ?? autoExpanded;

  const cardRef = useRef<HTMLDivElement>(null);

  const handleSendMessage = (text: string) => {
    // A card button that sends a message is the user asking something: take them to it. The card
    // usually sits above the fold of a long answer, so without this the new message and the reply
    // land out of sight and the press looks like it did nothing (PLT-6906: one user pressed seven
    // times). Dispatched before the append, so the lock is already following when the new
    // message's resize and assistant-ui's run-start scroll arrive.
    cardRef.current?.closest('.aui-thread-viewport')?.dispatchEvent(new Event(FOLLOW_THREAD_EVENT));
    runtime.thread.append(text);
  };

  useLiveJobStatus({
    jobId: extractJobId(toolResult),
    initialPayload: toolResult,
    onUpdate: payload => iframeHandleRef.current?.postLiveUpdate(payload),
  });

  if (bare) {
    return (
      <div ref={cardRef}>
        <McpAppIframe
          ref={iframeHandleRef}
          appName={appName}
          toolResult={toolResult}
          onSendMessage={handleSendMessage}
        />
      </div>
    );
  }

  const titleLabel = label ?? prettifyAppName(appName);
  // Glanceable headline figure shown right-aligned on the collapsed card on
  // intermediate+ surfaces (PLT-4997) — e.g. "+49%" for net impact, "9/16" for
  // DMA material matters, "12/18" for SFDR PAIs. `null` for apps without an
  // obvious headline figure; the CSS hides it on mobile / in the narrow side
  // panel and while the card is expanded. Each figure carries its metric label
  // (Janne: "add labels to the values so we know what they mean") and an
  // optional valence `tone` that sign-colours net-impact figures.
  const preview = getMcpAppCardPreview(appName, toolResult);
  return (
    <div ref={cardRef} className={`chat-mcp-app-card${expanded ? ' chat-mcp-app-card--expanded' : ''}`}>
      <button
        type="button"
        className="chat-mcp-app-card-toggle"
        onClick={() => {
          setUserToggled(!expanded);
        }}
        aria-expanded={expanded}
      >
        <span className="chat-mcp-app-card-toggle-row">
          <span className="chat-mcp-app-card-toggle-icon" aria-hidden="true">
            {/*
             * Apple-style disclosure chevron: points right (`>`) when
             * collapsed and rotates 90° to point down (`v`) when expanded.
             * Path is the right-facing chevron drawn at a 12×12 viewBox; the
             * 90° rotation lives in CSS so the transition is animatable.
             */}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M4.5 3 L7.5 6 L4.5 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="chat-mcp-app-card-toggle-label">{titleLabel}</span>
          {preview && (
            <span className="chat-mcp-app-card-preview">
              {preview.map(segment => (
                <span key={segment.label} className="chat-mcp-app-card-preview-item">
                  <span className="chat-mcp-app-card-preview-label">{segment.label}</span>
                  {/* `data-tone` drives the value's valence colour (net impact
                      positive/negative); omitted for neutral figures, where
                      React drops the undefined attribute. */}
                  <span className="chat-mcp-app-card-preview-value" data-tone={segment.tone}>
                    {segment.value}
                    {/* The `/total` reference of a ratio figure, rendered a touch
                        lighter than the numerator (Janne, PLT-4997). Omitted for
                        non-ratio figures. */}
                    {segment.reference && (
                      <span className="chat-mcp-app-card-preview-reference">{segment.reference}</span>
                    )}
                  </span>
                </span>
              ))}
            </span>
          )}
          <span className="chat-mcp-app-card-toggle-action">{expanded ? 'Hide' : 'Show'}</span>
        </span>
      </button>
      <div className="chat-mcp-app-card-body">
        <McpAppIframe
          ref={iframeHandleRef}
          appName={appName}
          toolResult={toolResult}
          onSendMessage={handleSendMessage}
        />
      </div>
    </div>
  );
}
